import { Injectable } from '@angular/core';
import { gql } from 'apollo-angular';
import { Observable, Subscription, combineLatest, filter, of } from 'rxjs';
import { distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { SoftBanFacade } from 'src/app/core/state/facades/soft-ban.facade';
import { SoftBanModalComponent } from 'src/app/shared/components/soft-ban-modal/soft-ban-modal.component';
import { User } from 'src/app/shared/models/gql/user.model';
import { AccountService } from 'src/app/shared/services/account.service';
import { ApiInterfaceService } from 'src/app/shared/services/api-interface.service';
import { AuthService } from 'src/app/shared/services/auth.service';
import { distinctUntilObjectChanged } from 'src/app/shared/utilities/rxjs/distinct-until-object-changed';
import { ModalDialogService } from './modal-dialog.service';

/**
 * This service is used by soft-ban guard and can be used application wide.
 * Helps to determine whether the current user has been soft banned or not.
 *
 * This service is able to get real-time updates from the backend using action cable.
 * It also maintains the soft ban state at the application level.
 *
 * Additionally, helps to show soft ban modal only once per given page (url path).
 *
 * Provides users ban status as
 * observable:  isSoftBanActive$.
 *
 */

@Injectable({
  providedIn: 'root',
})
export class SoftBanService {
  private accountServiceSub: Subscription; // Stores subscription for userAccount observable.

  constructor(
    private auth: AuthService,
    private api: ApiInterfaceService,
    private accountService: AccountService,
    private modalDialogService: ModalDialogService,
    private softBanFacade: SoftBanFacade,
  ) {}

  /**
   * Returns soft ban status observable and runs initialisation func if soft ban status is yet unknown
   * it may happen when service is injected by component directly by subscribing to this observable.
   */
  get isSoftBanActive$(): Observable<boolean> {
    return this.softBanFacade.isSoftBanActive$;
  }

  /**
   * Subscribe to account channel to get soft ban details. Listen to account channel user subject to get updated soft ban details.
   * It has been desired not to create a separate channel for soft ban flag only, hence we are reusing account channel to update the flag.
   */
  private subscribeToAccountChannel(): void {
    this.accountServiceSub = this.accountService.user$.pipe(distinctUntilChanged()).subscribe((userAccount) => {
      const softBanStatus = userAccount?.hasActiveSoftBan;
      if (softBanStatus !== undefined) {
        this.softBanFacade.setSoftBanStatus('Action Cable', softBanStatus);
      }
    });
  }

  /**
   * Unsubscribe from account channel.
   */
  private unsubscribeFromAccountChannel(): void {
    this.accountServiceSub?.unsubscribe();
  }

  /**
   * For subscribing to action cable soft ban status changes.
   * Also runs initialisation of the soft ban status if user is authenticated.
   * Note that this needs to be run only once per app initialisation.
   */
  public initSoftBanFeature(): void {
    const authUserIsLoggedIn$ = this.auth.isUserLoggedIn$.pipe(switchMap(() => of(this.auth.isUserLoggedIn())));

    combineLatest({
      isInitialised: this.auth.isInitialised$,
      isUserLoggedIn: authUserIsLoggedIn$,
    })
      .pipe(
        distinctUntilObjectChanged(),
        filter(({ isInitialised }) => isInitialised),
      )
      .subscribe(({ isUserLoggedIn }) => {
        if (isUserLoggedIn) {
          // As the service data is not destroyed on logout, we need to reset the soft ban data on logout/session expiry.
          // This is to ensure that service has the current soft ban status and not the previous one.
          this.softBanFacade.initSoftBanStatus('SoftBanService');
          this.subscribeToAccountChannel();
        } else {
          this.unsubscribeFromAccountChannel();
          this.softBanFacade.resetSoftBanStatus('SoftBanService');
        }
      });
  }

  /**
   * For querying soft ban status from BE.
   */
  public getSoftBanStatus$(): Observable<boolean> {
    // TODO (ES-19856): Replace Query Type with the one generated from GQL Schema
    type Query = { user: User };
    return this.api
      .gqlQuery$<Query>(gql`
        query {
          user {
            hasActiveSoftBan
          }
        }
      `)
      .pipe(map((result) => result.user?.hasActiveSoftBan));
  }

  /**
   * Presents soft ban modal to a user to notify that their account has been suspended/restricted.
   */
  openSoftBanModal(): void {
    const opts = {
      component: SoftBanModalComponent,
      cssClass: 'fullpage-modal',
    };
    this.modalDialogService.addDialogToQueue(opts);
  }
}
